import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from './cn';
import { controlStyles, type ControlSize } from './controlStyles';
import { useFieldControl } from './fieldContext';

export interface InputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'size'
> {
  /** Control height: `sm` (32px) for toolbars and tables, `md` (36px) for forms. */
  controlSize?: ControlSize;
  /** Monospace digits for coordinates and other numeric data. */
  mono?: boolean;
  /** Decorative icon inside the start of the field, e.g. a search glass. */
  leadingIcon?: ReactNode;
  /** Element inside the end of the field, e.g. a unit label or clear button. */
  trailing?: ReactNode;
  /** Classes for the wrapper rendered when an icon or trailing element is set. */
  containerClassName?: string;
}

/** Single-line text field. Inside a `Field`, it is labelled automatically. */
export const Input = /* @__PURE__ */ forwardRef<HTMLInputElement, InputProps>(
  function Input(
    {
      controlSize = 'md',
      mono = false,
      leadingIcon,
      trailing,
      containerClassName,
      className,
      type = 'text',
      ...props
    },
    ref
  ) {
    const controlProps = useFieldControl(props);
    const input = (
      <input
        ref={ref}
        type={type}
        className={controlStyles({
          size: controlSize,
          mono,
          className: cn(
            'enabled:read-only:bg-surface-sunken',
            leadingIcon != null && (controlSize === 'sm' ? 'pl-8' : 'pl-9'),
            trailing != null && 'pr-9',
            className
          ),
        })}
        {...controlProps}
      />
    );
    if (leadingIcon == null && trailing == null) return input;
    return (
      <div className={cn('relative', containerClassName)}>
        {leadingIcon != null && (
          <span
            aria-hidden="true"
            className={cn(
              'pointer-events-none absolute inset-y-0 flex items-center text-fg-muted [&_svg]:size-4',
              controlSize === 'sm' ? 'left-2.5' : 'left-3'
            )}
          >
            {leadingIcon}
          </span>
        )}
        {input}
        {trailing != null && (
          <span className="absolute inset-y-0 right-1 flex items-center text-fg-muted">
            {trailing}
          </span>
        )}
      </div>
    );
  }
);
